import T from 'Components/Helpers/translate';
import type { AgentApproval } from 'Resources/AgentApprovals';
import type {
	ContactScrapeConfig,
	EmailDraft,
	MultiActionConfig,
	OfferDraft,
	OfferDraftLineItem,
	ProspectingAddConfig,
	WriteProposal
} from './types';

/**
 * `crmMutation` is emitted under a few historical action names; all three render
 * as a WriteCard. Kept next to `approvalToWriteProposal`, which is what they all
 * resolve to, so a surface cannot pick up the adapter and miss two of the names —
 * which is how the ui-3 inbox ended up only handling `crmMutation` (WEB-11464).
 */
export const CRM_ACTIONS = new Set(['crmMutation', 'proposeCrmChange', 'writeCRM']);

/**
 * Once a card reports one of these the approval is settled and the surface
 * showing it can drop or refetch the row.
 *
 * `error` and `failed` are deliberately absent. Both put the card into a state
 * that offers Retry, and both arrive when the approval row is no longer
 * `pending` server-side — so a surface that refetched on them would unmount the
 * only place the failure and its retry are shown.
 */
export const RESOLVED_STATUSES = new Set(['saved', 'sent', 'cancelled', 'completed']);

/** Infer the multi-action task from the registry's actionName prefix when
 *  the actionPayload doesn't carry `task` directly (taskRunner stores the
 *  raw AI tool input, which has task; agentEngine stores the registry-derived
 *  shape, which also has task — but we've also seen older rows missing it).
 *  Prefers an explicit task; falls back to the action prefix; defaults to
 *  Update only as a last resort.
 */
const inferTask = (rawTask: unknown, rawAction: unknown): MultiActionConfig['task'] => {
	if (rawTask === 'Create' || rawTask === 'Update' || rawTask === 'Delete') return rawTask;
	const a = typeof rawAction === 'string' ? rawAction : '';
	if (a.startsWith('Create')) return 'Create';
	if (a.startsWith('Update')) return 'Update';
	if (a.startsWith('Delete')) return 'Delete';
	return 'Update';
};

/** The approval row's payload columns are JSON-stringified in MySQL. The
 *  resource (AgentApprovalsResource) already parses them when fetching, but
 *  we still need a defensive parse for any caller that hands us a row that
 *  bypassed the resource (legacy API responses, manual inlining, tests, …). */
export const parseApprovalPayload = (raw: unknown): Record<string, unknown> | null => {
	if (raw == null) return null;
	if (typeof raw === 'string') {
		try {
			return JSON.parse(raw) as Record<string, unknown>;
		} catch {
			return null;
		}
	}
	if (typeof raw === 'object') return raw as Record<string, unknown>;
	return null;
};

/** A resolved approval can carry an execution-failure signal in two places:
 *  the row's status (when the orchestrator caught a thrown error or
 *  promoted an executor-returned `{error: …}` to a rejection) OR the
 *  resultPayload (when an auto-approved pre-write executed and the
 *  outcome was stamped after). Both surfaces should render the proposal
 *  card as Failed with the executor's message so the user can see what
 *  went wrong instead of a misleading green checkmark. */
export const executionFailureMessage = (result: Record<string, unknown> | null | undefined): string | null => {
	if (!result) return null;
	const err = result.error;
	if (typeof err === 'string' && err.length > 0) return err;
	if (result.success === false) {
		const msg = result.message;
		// `success: false` without a concrete error message can happen on
		// user-rejected / cancelled / expired crmMutation rows because
		// extractApprovalResult hard-codes `success: false` whenever
		// outcome !== 'approved'. Return null there so the caller falls
		// through to the status switch and renders as cancelled, not error.
		return typeof msg === 'string' && msg.length > 0 ? msg : null;
	}
	return null;
};

/** Map an `agent_approval_request` row (action=crmMutation / proposeCrmChange /
 *  writeCRM) back to the in-memory WriteProposal shape used by WriteCard. */
export const approvalToWriteProposal = (a: AgentApproval): WriteProposal => {
	const payload = (parseApprovalPayload(a.actionPayload) ?? {}) as {
		entity?: string;
		action?: string;
		data?: Record<string, unknown>;
		matchingContacts?: number;
		entityDisplayName?: string;
		mergeDisplayName?: string;
	};
	const modified = parseApprovalPayload(a.modifiedPayload) as {
		data?: Record<string, unknown>;
	} | null;
	const data = (modified?.data ?? payload.data ?? {}) as Record<string, unknown>;
	const failure = executionFailureMessage(a.resultPayload as Record<string, unknown> | null | undefined);
	let status: WriteProposal['status'];
	if (failure !== null) {
		status = 'error';
	} else {
		switch (a.status) {
			case 'approved':
				status = 'saved';
				break;
			case 'rejected':
			case 'changes_requested':
			case 'expired':
				status = 'cancelled';
				break;
			default:
				status = 'pending';
		}
	}
	return {
		entity: String(payload.entity ?? ''),
		action: (payload.action as WriteProposal['action']) ?? 'create',
		data,
		status,
		matchingContacts: typeof payload.matchingContacts === 'number' ? payload.matchingContacts : undefined,
		entityDisplayName: typeof payload.entityDisplayName === 'string' ? payload.entityDisplayName : undefined,
		mergeDisplayName: typeof payload.mergeDisplayName === 'string' ? payload.mergeDisplayName : undefined,
		approvalId: a.id,
		resultEntityType: a.resultEntityType,
		resultEntityId: a.resultEntityId,
		result: a.resultPayload ?? undefined,
		error: failure || undefined
	};
};

/** Map an `agent_approval_request` row (action=multiAction) back to the
 *  in-memory MultiActionConfig that MultiActionWriteCard renders. */
export const approvalToMultiActionConfig = (a: AgentApproval): MultiActionConfig => {
	const payload = (parseApprovalPayload(a.actionPayload) ?? {}) as Partial<MultiActionConfig>;
	const modified = parseApprovalPayload(a.modifiedPayload) as Partial<MultiActionConfig> | null;
	const merged = { ...payload, ...(modified ?? {}) };
	const failure = executionFailureMessage(a.resultPayload as Record<string, unknown> | null | undefined);
	let status: MultiActionConfig['status'];
	if (failure !== null) {
		status = 'failed';
	} else {
		switch (a.status) {
			case 'approved':
				status = 'completed';
				break;
			case 'rejected':
			case 'changes_requested':
			case 'expired':
				status = 'cancelled';
				break;
			default:
				status = 'pending';
		}
	}

	const task = inferTask(merged.task, merged.action);
	const entity = String(merged.entity ?? '');

	// Pull the run summary off resultPayload so the resolved card surfaces a
	// short "Created 9 of 9 activities" message even on first render after
	// reload (no live handleConfirm to set resultMessage). Use the same lang
	// keys the live handler uses so a Swedish customer doesn't see English
	// after a reload.
	let resultMessage: string | undefined;
	if (status === 'completed' && a.resultPayload && typeof a.resultPayload === 'object') {
		const rp = a.resultPayload as { totalChanged?: number | null; total?: number | null };
		const totalChanged = typeof rp.totalChanged === 'number' ? rp.totalChanged : null;
		const total = typeof rp.total === 'number' ? rp.total : null;
		const actionPast =
			task === 'Delete'
				? T('assistant.actionDeleted') || 'Deleted'
				: task === 'Create'
				? T('assistant.actionCreated') || 'Created'
				: T('assistant.actionUpdated') || 'Updated';
		const entityWord = entity ? entity.toLowerCase() : T('assistant.records') || 'records';
		const ofWord = (T('default.of') || 'of').toLowerCase();
		if (totalChanged != null && total != null) {
			resultMessage = `${actionPast} ${totalChanged} ${ofWord} ${total} ${entityWord}.`;
		} else if (totalChanged != null) {
			resultMessage = `${actionPast} ${totalChanged} ${entityWord}.`;
		}
	}

	return {
		id: merged.id ?? `ap_${a.id}`,
		task,
		entity,
		action: String(merged.action ?? ''),
		entityRoute: String(merged.entityRoute ?? ''),
		filters: Array.isArray(merged.filters) ? merged.filters : [],
		properties: Array.isArray(merged.properties) ? merged.properties : [],
		selectedIds: Array.isArray(merged.selectedIds) ? merged.selectedIds : [],
		estimatedCount: Number(merged.estimatedCount ?? 0),
		description: String(merged.description ?? ''),
		status,
		resultMessage,
		approvalId: a.id,
		resultEntityType: a.resultEntityType,
		resultEntityId: a.resultEntityId
	};
};

/** Map an `agent_approval_request` row (action=sendEmail) back to the
 *  in-memory EmailDraft shape used by EmailDraftCard. */
export const approvalToEmailDraft = (a: AgentApproval): EmailDraft => {
	const payload = (parseApprovalPayload(a.actionPayload) ?? {}) as {
		contactIds?: unknown;
		subject?: unknown;
		body?: unknown;
		ccContactIds?: unknown;
		clientId?: unknown;
		attachments?: unknown;
	};
	const modified = parseApprovalPayload(a.modifiedPayload) as typeof payload | null;
	const merged = { ...payload, ...(modified ?? {}) };
	// WEB-10524: an approval created before the tool boundary normalised its id
	// lists holds the model's JSON-string form ("[31094]"). Parse it rather
	// than degrading to [] — an empty list renders a draft with no recipients,
	// and the card then echoes that emptiness back on approve, tripping the
	// recipients-immutability guard. Reading these rows correctly is what makes
	// the already-stored drafts sendable without a backfill.
	const toNumberArray = (raw: unknown): number[] => {
		let list = raw;
		if (typeof list === 'string') {
			try {
				list = JSON.parse(list);
			} catch {
				return [];
			}
		}
		if (typeof list === 'number') {
			list = [list];
		}
		return Array.isArray(list) ? list.map(v => Number(v)).filter(n => Number.isFinite(n)) : [];
	};
	const toAttachments = (raw: unknown): { id: number; name: string }[] | undefined =>
		Array.isArray(raw)
			? raw
					.map(a => (a && typeof a === 'object' ? (a as { id?: unknown; name?: unknown }) : {}))
					.filter(a => Number.isInteger(Number(a.id)) && Number(a.id) > 0)
					.map(a => ({ id: Number(a.id), name: typeof a.name === 'string' ? a.name : `file-${a.id}` }))
			: undefined;
	const failure = executionFailureMessage(a.resultPayload as Record<string, unknown> | null | undefined);
	let status: EmailDraft['status'];
	if (failure !== null) {
		status = 'error';
	} else {
		switch (a.status) {
			case 'approved':
				status = 'sent';
				break;
			case 'rejected':
			case 'changes_requested':
			case 'expired':
				status = 'cancelled';
				break;
			default:
				status = 'pending';
		}
	}
	const resultMailIds = Array.isArray(a.resultPayload?.mailIds)
		? (a.resultPayload!.mailIds as unknown[]).map(v => Number(v)).filter(n => Number.isFinite(n))
		: undefined;
	return {
		contactIds: toNumberArray(merged.contactIds),
		subject: typeof merged.subject === 'string' ? merged.subject : '',
		body: typeof merged.body === 'string' ? merged.body : '',
		ccContactIds: toNumberArray(merged.ccContactIds),
		clientId: typeof merged.clientId === 'number' ? merged.clientId : undefined,
		attachments: toAttachments(merged.attachments),
		status,
		approvalId: a.id,
		mailIds: resultMailIds?.length ? resultMailIds : undefined,
		error: failure || undefined
	};
};

/** Map an `agent_approval_request` row (action=draftOffer) back to the
 *  in-memory OfferDraft shape used by OfferDraftCard. Line items aren't
 *  editable in the card, so there's no modifiedPayload to merge. */
export const approvalToOfferDraft = (a: AgentApproval): OfferDraft => {
	const payload = (parseApprovalPayload(a.actionPayload) ?? {}) as {
		clientId?: unknown;
		contactId?: unknown;
		products?: unknown;
		currency?: unknown;
		validUntil?: unknown;
		description?: unknown;
		notes?: unknown;
	};
	const toProducts = (raw: unknown): OfferDraftLineItem[] =>
		Array.isArray(raw)
			? raw.map(p => {
					const r = (p && typeof p === 'object' ? p : {}) as Record<string, unknown>;
					return {
						productId: Number(r.productId),
						productName: typeof r.productName === 'string' ? r.productName : undefined,
						quantity: Number(r.quantity),
						...(typeof r.unitPrice === 'number' ? { unitPrice: r.unitPrice } : {}),
						...(typeof r.discount === 'number' ? { discount: r.discount } : {})
					};
			  })
			: [];
	const failure = executionFailureMessage(a.resultPayload as Record<string, unknown> | null | undefined);
	let status: OfferDraft['status'];
	if (failure !== null) {
		status = 'error';
	} else {
		switch (a.status) {
			case 'approved':
				status = 'saved';
				break;
			case 'rejected':
			case 'changes_requested':
			case 'expired':
				status = 'cancelled';
				break;
			default:
				status = 'pending';
		}
	}
	return {
		clientId: Number(payload.clientId),
		contactId: Number(payload.contactId),
		products: toProducts(payload.products),
		currency: typeof payload.currency === 'string' ? payload.currency : '',
		validUntil: typeof payload.validUntil === 'string' ? payload.validUntil : '',
		description: typeof payload.description === 'string' ? payload.description : undefined,
		notes: typeof payload.notes === 'string' ? payload.notes : undefined,
		status,
		approvalId: a.id,
		resultEntityType: a.resultEntityType,
		resultEntityId: a.resultEntityId,
		error: failure || undefined
	};
};

// Rebuild a contact-discovery card from its persisted approval row, so the card
// survives a reload the way the multi-action card does (WEB-11112). Without
// this the assistant's reply still says "there's a card to approve" while the
// card itself is gone — which is what the first real run of this tool showed.
/** The card state a single-action approval row is in: failed, done, dropped or still waiting. */
const approvalCardStatus = (a: AgentApproval, failure: string | null): ContactScrapeConfig['status'] => {
	if (failure !== null) return 'failed';
	switch (a.status) {
		case 'approved':
			return 'completed';
		case 'rejected':
		case 'changes_requested':
		case 'expired':
			return 'cancelled';
		default:
			return 'pending';
	}
};

export const approvalToContactScrapeConfig = (a: AgentApproval): ContactScrapeConfig => {
	const payload = (parseApprovalPayload(a.actionPayload) ?? {}) as Partial<ContactScrapeConfig>;
	const failure = executionFailureMessage(a.resultPayload as Record<string, unknown> | null | undefined);
	const status = approvalCardStatus(a, failure);

	// The queued job's notification lives on the RESULT payload, not the proposal
	// the agent made — without it a card rebuilt after a reload has nothing to
	// follow and stays on "running" for work that already finished.
	const executed = (parseApprovalPayload(a.resultPayload) ?? {}) as { notificationId?: unknown };
	const notificationId = Number(executed.notificationId);

	const clientIds = Array.isArray(payload.clientIds) ? payload.clientIds : [];
	const companyCount = Number(payload.companyCount) || clientIds.length;

	return {
		id: String(payload.id ?? `cs_approval_${a.id}`),
		clientIds,
		companyCount,
		sampleNames: Array.isArray(payload.sampleNames) ? payload.sampleNames : [],
		skippedNoWebsite: Number(payload.skippedNoWebsite) || 0,
		cost: payload.cost,
		notificationId: Number.isFinite(notificationId) && notificationId > 0 ? notificationId : undefined,
		status,
		resultMessage:
			failure ??
			(status === 'completed' ? T('assistant.contactScrape.started', { count: companyCount }) : undefined),
		approvalId: a.id
	};
};

export const PROSPECTING_ADD_ACTIONS = new Set([
	'prospecting_add_company',
	'prospecting_add_contacts',
	'prospecting_add_companies_bulk'
]);

/** The card variant for an approval or stream action; anything unknown reads as one company. */
export const toProspectingAddAction = (action: string): ProspectingAddConfig['action'] =>
	action === 'prospecting_add_contacts' || action === 'prospecting_add_companies_bulk'
		? action
		: 'prospecting_add_company';

/** A drafted prospecting add (WEB-11807), as the card that approves it reads it. */
/**
 * The prospecting import matches on org number, so adding a branch of a company already in the
 * CRM links that record instead of creating one (WEB-14401). The result says so with
 * `created: false`; rows written before the flag existed don't carry it and keep "Added".
 */
export const linkedExistingCompany = (
	a: Pick<AgentApproval, 'action' | 'status' | 'resultEntityId' | 'resultPayload'>
) =>
	a.action === 'prospecting_add_company' &&
	a.status === 'approved' &&
	Number(a.resultEntityId) > 0 &&
	(a.resultPayload as { created?: unknown } | null | undefined)?.created === false;

export const approvalToProspectingAdd = (a: AgentApproval): ProspectingAddConfig => {
	const payload = (parseApprovalPayload(a.actionPayload) ?? {}) as {
		name?: unknown;
		orgNumber?: unknown;
		contacts?: unknown;
		// Bulk add (WEB-12974)
		prospectingIds?: unknown;
		campaign?: { name?: unknown } | null;
		credits?: { needed?: unknown } | null;
		sample?: unknown;
		accountManager?: { name?: unknown } | null;
		country?: unknown;
	};
	const failure = executionFailureMessage(a.resultPayload as Record<string, unknown> | null | undefined);
	const contacts = Array.isArray(payload.contacts)
		? payload.contacts
				.map(c => (c && typeof c === 'object' ? (c as Record<string, unknown>) : {}))
				.filter(c => typeof c.name === 'string' && c.name)
				.map(c => ({ name: String(c.name), ...(typeof c.role === 'string' ? { role: c.role } : {}) }))
		: [];
	return {
		id: `pa_approval_${a.id}`,
		action: toProspectingAddAction(a.action),
		companyName: typeof payload.name === 'string' ? payload.name : undefined,
		orgNumber: typeof payload.orgNumber === 'string' ? payload.orgNumber : undefined,
		contacts,
		...(Array.isArray(payload.prospectingIds) ? { companyCount: payload.prospectingIds.length } : {}),
		...(typeof payload.campaign?.name === 'string' && payload.campaign.name
			? { campaignName: payload.campaign.name }
			: {}),
		...(typeof payload.credits?.needed === 'number' ? { credits: payload.credits.needed } : {}),
		...(Array.isArray(payload.sample)
			? { sample: payload.sample.filter((name): name is string => typeof name === 'string') }
			: {}),
		...(typeof payload.accountManager?.name === 'string' && payload.accountManager.name
			? { accountManagerName: payload.accountManager.name }
			: {}),
		...(typeof payload.country === 'string' && payload.country ? { country: payload.country } : {}),
		status: approvalCardStatus(a, failure),
		resultMessage: failure ?? undefined,
		clientId: a.resultEntityId ?? undefined,
		...(failure === null && linkedExistingCompany(a) ? { linkedExisting: true } : {}),
		approvalId: a.id
	};
};
